/**
 * Market + stock visibility helpers (DHA-41) — shared by editor, server, and preview.
 * generalSettings.condition.market / generalSettings.condition.stocks
 *
 * hybrid.js is an IIFE and cannot import this file; it carries small inline copies.
 */

export const MARKET_MODES = Object.freeze({
  ALL: "all",
  SPECIFIC: "specific",
});

/** How out-of-stock offer products appear in the showcase. */
export const OOS_BEHAVIORS = Object.freeze({
  HIDE: "hide",
  BADGE: "badge",
  DISABLE: "disable",
});

export const DEFAULT_SOLD_OUT_OFFER_MESSAGE = "Sold out";
export const DEFAULT_PREVIEW_OUT_OF_STOCK_MESSAGE = "Out of stock";

function normalizeCountryCodes(codes) {
  if (!Array.isArray(codes)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of codes) {
    const code = String(raw ?? "").trim().toUpperCase();
    if (code && !seen.has(code)) {
      seen.add(code);
      out.push(code);
    }
  }
  return out;
}

/**
 * @param {object | null | undefined} condition generalSettings.condition
 * @returns {{ mode: 'all'|'specific', marketId: string, marketName: string, currencyCode: string, countryCodes: string[] }}
 */
export function normalizeMarketCondition(condition) {
  const raw = condition?.market;
  const mode =
    raw && String(raw.mode || "").trim() === MARKET_MODES.SPECIFIC
      ? MARKET_MODES.SPECIFIC
      : MARKET_MODES.ALL;
  if (mode === MARKET_MODES.ALL) {
    return {
      mode,
      marketId: "",
      marketName: "",
      currencyCode: "",
      countryCodes: [],
    };
  }
  return {
    mode,
    marketId: raw.marketId != null ? String(raw.marketId) : "",
    marketName: raw.marketName != null ? String(raw.marketName) : "",
    currencyCode: String(raw.currencyCode || "")
      .trim()
      .toUpperCase(),
    countryCodes: normalizeCountryCodes(raw.countryCodes),
  };
}

/**
 * @param {object | null | undefined} market normalized or raw market ({ mode, countryCodes })
 * @param {string} shopperCountryCode ISO-3166 alpha-2
 */
export function isMarketMatch(market, shopperCountryCode) {
  if (!market || typeof market !== "object") return true;
  const mode = String(market.mode || "").trim();
  if (mode !== MARKET_MODES.SPECIFIC) return true;
  const codes = normalizeCountryCodes(market.countryCodes);
  if (!codes.length) return false;
  const shopper = String(shopperCountryCode || "").trim().toUpperCase();
  if (!shopper) return false;
  return codes.includes(shopper);
}

/** 0 = all markets, 1 = specific market. */
export function marketSpecificityScore(market) {
  return market && String(market.mode || "").trim() === MARKET_MODES.SPECIFIC
    ? 1
    : 0;
}

/**
 * Within a family of same-type widgets, if any market-specific one is present
 * drop the all-markets ones. Input should already be market-matched.
 * Without a family function, all items are treated as one family — callers
 * should group first (see getFamily).
 * @param {any[]} popups
 * @param {(popup: any) => object | undefined} getMarketFn
 * @param {(popup: any) => string} [getFamily]
 */
export function pickBestByMarket(popups, getMarketFn, getFamily) {
  if (!Array.isArray(popups) || popups.length < 2) return popups || [];
  const marketOf = typeof getMarketFn === "function" ? getMarketFn : () => null;
  const familyOf = typeof getFamily === "function" ? getFamily : () => "";
  const bestByFamily = new Map();
  for (const popup of popups) {
    const fam = familyOf(popup);
    const score = marketSpecificityScore(marketOf(popup));
    if (score > (bestByFamily.get(fam) ?? 0)) bestByFamily.set(fam, score);
  }
  return popups.filter(
    (popup) =>
      marketSpecificityScore(marketOf(popup)) >=
      (bestByFamily.get(familyOf(popup)) ?? 0)
  );
}

/**
 * @param {object | null | undefined} condition generalSettings.condition
 */
export function normalizeStocksCondition(condition) {
  const raw = condition?.stocks || {};
  const preview = String(raw.previewOutOfStockMessage ?? "").trim();
  const soldOut = String(raw.soldOutOfferMessage ?? "").trim();
  const oosRaw = String(raw.oosBehavior || "").trim().toLowerCase();
  const oosBehavior = Object.values(OOS_BEHAVIORS).includes(oosRaw)
    ? oosRaw
    : OOS_BEHAVIORS.HIDE;
  return {
    previewOutOfStockEnabled: raw.previewOutOfStockEnabled === true,
    previewOutOfStockMessage: preview || DEFAULT_PREVIEW_OUT_OF_STOCK_MESSAGE,
    soldOutOfferMessage: soldOut || DEFAULT_SOLD_OUT_OFFER_MESSAGE,
    oosBehavior,
  };
}

export function getOosBehavior(condition) {
  return normalizeStocksCondition(condition).oosBehavior;
}

export function getSoldOutOfferMessage(condition) {
  return normalizeStocksCondition(condition).soldOutOfferMessage;
}

export function getPreviewOutOfStockMessage(condition) {
  return normalizeStocksCondition(condition).previewOutOfStockMessage;
}
